import { Form, Input, Modal, Spin } from 'antd';
import classNames from 'classnames';
import React, { memo, useCallback, useMemo, useState } from 'react';
import DispexButton from '../../../../components/DispexButton/DispexButton';
import Message from '../../../../components/Message/Message';
import usePassword from '../../../../hooks/usePassword';
import recoverPassword from '../../requests/recoverPassword';
import sendRecoverCode from '../../requests/sendRecoverCode';
import './ForgotPassword.scss';

const ForgotPassword = ({ setForgotPasswordModalOpened, defaultUsername }) => {
  const [login, setLogin] = useState(defaultUsername);
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [sendCodeError, setSendCodeError] = useState(false);
  const [code, setCode] = useState('');
  const [
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    errors,
  ] = usePassword();
  const savingPasswordDisabled =
    !codeSent || errors || sendCodeError || !newPassword || !confirmPassword;

  const onExit = useCallback(() => {
    setForgotPasswordModalOpened(false);
  }, []);
  const onOk = useCallback(() => {
    if (!savingPasswordDisabled) {
      recoverPassword({
        newPassword,
        confirmPassword,
        code,
        login,
        onExit,
      });
    }
  }, [
    savingPasswordDisabled,
    newPassword,
    confirmPassword,
    code,
    login,
    onExit,
  ]);

  const onLoginChange = useCallback(
    (e) => {
      setLogin(e.target.value);
      if (codeSent) {
        setCode('');
        setCodeSent(false);
        setNewPassword('');
        setConfirmPassword('');
      }
    },
    [codeSent]
  );
  const onCodeChange = useCallback((e) => setCode(e.target.value), []);
  const onNewPasswordChange = useCallback(
    (e) => setNewPassword(e.target.value),
    []
  );
  const onConfirmPasswordChange = useCallback(
    (e) => setConfirmPassword(e.target.value),
    []
  );
  const okButtonProps = useMemo(
    () => ({ disabled: savingPasswordDisabled }),
    [savingPasswordDisabled]
  );
  const sendCode = useCallback(async () => {
    if (login) {
      setLoading(true);
      const successCodeSent = await sendRecoverCode(login);
      setCodeSent(successCodeSent);
      setSendCodeError(!successCodeSent);
      setLoading(false);
    }
  }, [login]);
  return (
    <Modal
      visible
      title="Восстановление пароля"
      onOk={onOk}
      okButtonProps={okButtonProps}
      className="forgot_password_modal"
      width="590px"
      onCancel={onExit}
    >
      <Spin spinning={loading}>
        <Message
          error={
            sendCodeError && (
              <span>
                Такого аккаунта нет или к нему не привязана почта, обратитесь к
                администратору:{' '}
                <a href="mailto:support@dispex.ru?subject=Восстановление пароля">
                  support@dispex.ru
                </a>
              </span>
            )
          }
        />

        <div className="row">
          <div className="column">
            <Form.Item label="Введите логин" colon={false}>
              <Input value={login} onChange={onLoginChange} />
            </Form.Item>
          </div>
          <div className="column">
            <Form.Item
              label="Введите код из почты"
              colon={false}
              className={classNames({ label_disabled: !codeSent })}
            >
              <Input
                placeholder="Код"
                disabled={!codeSent}
                value={code}
                onChange={onCodeChange}
              />
            </Form.Item>
          </div>
        </div>
        <div className="row">
          <DispexButton disabled={!login} type="primary" onClick={sendCode}>
            Отправить код на почту
          </DispexButton>
        </div>
        <div className="row">
          <div className="column">
            <Form.Item
              label="Придумайте новый пароль"
              colon={false}
              className={classNames({ label_disabled: codeSent })}
              validateStatus={errors ? 'error' : 'success'}
              help={
                newPassword && confirmPassword && !errors && 'Пароли совпадают'
              }
            >
              <Input.Password
                placeholder="Ваш пароль"
                disabled={!codeSent || !code}
                value={newPassword}
                onChange={onNewPasswordChange}
              />
            </Form.Item>
          </div>
          <div className="column">
            <Form.Item
              label="Повторите пароль"
              validateStatus={errors ? 'error' : 'success'}
              colon={false}
              className={classNames({ label_disabled: codeSent })}
              help={
                newPassword && confirmPassword && !errors && 'Пароли совпадают'
              }
            >
              <Input.Password
                placeholder="Ваш пароль"
                disabled={!codeSent || !code}
                value={confirmPassword}
                onChange={onConfirmPasswordChange}
              />
            </Form.Item>
          </div>
        </div>
        <div className="row">
          {errors ? (
            <span className="password_errors_hint">{errors}</span>
          ) : (
            <span>
              Пароль должен быть не менее 8 символов без пробелов, содержать
              латинские буквы разных регистров, хотя бы одну цифру и специальный
              символ
            </span>
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default memo(ForgotPassword);
